import { messDoorClient, ImResponse, resolve } from 'services/api';
import { getLocalTimeZone } from 'utils/timeZone';

export function getVoiceOverview(body: ReviewAnalysis.OverviewFilter) {
  const { storeInfo, sentimentType, ...others } = body;
  return resolve(
    messDoorClient.post<ImResponse<ReviewAnalysis.VoiceOverview>>('/review_analyzer/v1/overview', {
      overviewRequest: {
        ...others,
        storeIds: storeInfo.list?.map(({ id }) => id) || [],
        timeZone: getLocalTimeZone(),
      },
      sentimentType,
    }),
  );
}

export function getNegativeOverview(body: ReviewAnalysis.OverviewFilter) {
  const { storeInfo, stars, ...others } = body;
  return resolve(
    messDoorClient.post<ImResponse<ReviewAnalysis.NegativeOverview>>(
      '/review_analyzer/v1/overview/negative',
      {
        overviewRequest: {
          ...others,
          storeIds: storeInfo.list?.map(({ id }) => id) || [],
          stars: stars.length ? stars : [1, 2, 3],
          timeZone: getLocalTimeZone(),
        },
      },
    ),
  );
}

export function getNegativeCategoryOverview(body: ReviewAnalysis.OverviewFilter) {
  const { storeInfo, stars, ...others } = body;
  return resolve(
    messDoorClient.post<ImResponse<ReviewAnalysis.NegativeCategoryOverview>>(
      '/review_analyzer/v1/overview/negative/attribution',
      {
        overviewRequest: {
          ...others,
          storeIds: storeInfo.list?.map(({ id }) => id) || [],
          stars: stars.length ? stars : [1, 2, 3],
          timeZone: getLocalTimeZone(),
        },
      },
    ),
  );
}

export function getNegativeItemOverview(body: ReviewAnalysis.OverviewFilter) {
  const { storeInfo, stars, ...others } = body;
  return resolve(
    messDoorClient.post<
      ImResponse<{
        otherPercent: string;
        negativeItemData: ReviewAnalysis.NegativeItem[];
      }>
    >('/review_analyzer/v1/overview/negative/item', {
      overviewRequest: {
        ...others,
        storeIds: storeInfo.list?.map(({ id }) => id) || [],
        stars: stars.length ? stars : [1, 2, 3],
        timeZone: getLocalTimeZone(),
      },
    }),
  );
}

export interface GetProblemListParams {
  onlyNewProblem: boolean;
  problemCategoryId?: number;
  pageInfo: {
    page: number;
    pageSize: number;
  };
  overviewRequest: ReviewAnalysis.OverviewFilter;
  sortOrder: 'DESC' | 'ASC';
  sentimentType?: 1 | 2;
}

export function getProblemList(body: GetProblemListParams) {
  const { storeInfo, ...others } = body.overviewRequest;
  return resolve(
    messDoorClient.post<
      ImResponse<{
        pageInfo: {
          page: number;
          pageSize: number;
          totalCount: number;
        };
        problemList: ReviewAnalysis.Problem[];
      }>
    >('/review_analyzer/v1/tags', {
      ...body,
      overviewRequest: {
        ...others,
        storeIds: storeInfo.list?.map(({ id }) => id) || [],
        timeZone: getLocalTimeZone(),
      },
    }),
  );
}

export function getItemProblemList(
  body: GetProblemListParams & { sort: 'REVIEW_COUNT' | 'BAD_COUNT' | 'BAD_RATE' },
) {
  const { storeInfo, ...others } = body.overviewRequest;
  return resolve(
    messDoorClient.post<
      ImResponse<{
        pageInfo: {
          page: number;
          pageSize: number;
          totalCount: number;
        };
        itemList: ReviewAnalysis.ItemProblem[];
      }>
    >('/review_analyzer/v1/tagsGroupByItem', {
      ...body,
      overviewRequest: {
        ...others,
        storeIds: storeInfo.list?.map(({ id }) => id) || [],
        timeZone: getLocalTimeZone(),
      },
    }),
  );
}

export function updateProblem(body: {
  id: string;
  remark: string;
  tagStatus: ReviewAnalysis.ProblemStatus;
  spuKey?: string;
}) {
  return messDoorClient.post('/review_analyzer/v1/tag/process', body);
}

export function getReviewList(reviewIds: string[]) {
  return resolve(
    messDoorClient.post<
      ImResponse<{
        reviewList: ReviewAnalysis.Review[];
      }>
    >('/review_analyzer/v1/reviews', { reviewIds }),
  );
}

export function getItems(body: {
  keyword: string;
  keywordType: 1 | 2 | 3;
  pageInfo: {
    page: number;
    pageSize: number;
  };
  overviewRequest: Omit<ReviewAnalysis.OverviewFilter, 'storeInfo' | 'spuKeys'> & {
    storeIds: string[];
  };
  sort?: 'TOTAL_COUNT_DESC' | 'BAD_COUNT_DESC' | 'BAD_RATE_DESC';
}) {
  return resolve(
    messDoorClient.post<
      ImResponse<{
        page?: {
          page: number;
          pageSize: number;
          totalCount: number;
        };
        itemData: ReviewAnalysis.Item[];
      }>
    >('/review_analyzer/v1/items', {
      ...body,
      overviewRequest: {
        ...body.overviewRequest,
        timeZone: getLocalTimeZone(),
      },
    }),
  );
}

export function getTimeRange() {
  return resolve(
    messDoorClient.get<
      ImResponse<{
        timeRange: 'LAST_7_DAY' | 'LAST_30_DAY';
      }>
    >('/review_analyzer/v1/timeRange'),
  );
}

export interface BuyerPageParams {
  pageNum: number;
  pageSize: number;
}

export interface BuyerSortParams {
  orderBy: 'create_time' | 'cumu_return_trade' | 'cumu_bad_comment';
  asc: boolean;
}

export interface BuyerFilterParams {
  filterBuyerNick: string;
  filterCumuReturnTrade?: number;
  filterReturnTradeRatio?: number;
  filterCumuBadComment?: number;
  filterBadCommentRatio?: number;
}

export interface BuyersResponse {
  buyers: ReviewAnalysis.Buyer[];
  total: number;
}

type BuyerBody = BuyerPageParams & Partial<BuyerSortParams> & Partial<BuyerFilterParams>;

export function getHighRiskBuyers(body: BuyerBody) {
  return resolve(
    messDoorClient.post<ImResponse<BuyersResponse>>(
      '/oversea-buyer-offline/v2/getHighRiskBuyers',
      body,
    ),
  );
}

export function getMaliciousBuyers(body: BuyerBody) {
  return resolve(
    messDoorClient.post<ImResponse<BuyersResponse>>(
      '/oversea-buyer-offline/v2/getMaliciousBuyers',
      body,
    ),
  );
}

export interface MarkHighRiskMaliciousBody
  extends Pick<ReviewAnalysis.Buyer, 'tunnelType' | 'region' | 'tradeBuyerId'> {
  isMalicious: boolean;
}

export function markHighRiskMalicious(body: MarkHighRiskMaliciousBody) {
  return messDoorClient.post('/oversea-buyer-offline/v2/markHighRiskMalicious', body);
}

export interface ExportBuyerBody {
  externalType?: General.PlatformUpperCaseTunnel;
  region?: General.UpperCaseRegion;
  beginDate: string;
  endDate: string;
}

export function exportHighRiskBuyer(body: ExportBuyerBody) {
  return resolve(
    messDoorClient.post<ImResponse<{ resultUrl: string }>>(
      '/oversea-buyer-offline/v2/exportHighRiskBuyer',
      body,
    ),
  );
}

export function exportMaliciousBuyer(body: ExportBuyerBody) {
  return resolve(
    messDoorClient.post<ImResponse<{ resultUrl: string }>>(
      '/oversea-buyer-offline/v2/exportMalBuyer',
      body,
    ),
  );
}

export interface ExportTradeBody {
  externalType?: General.PlatformUpperCaseTunnel;
  region?: General.UpperCaseRegion;
  externalStoreId?: string;
  tradeBeginDate: string;
  tradeEndDate: string;
}

export function exportMaliciousTrade(body: ExportTradeBody) {
  return resolve(
    messDoorClient.post<ImResponse<{ resultUrl: string }>>(
      '/oversea-buyer-offline/v2/exportMalBuyerTrade',
      body,
    ),
  );
}

export function importMaliciousBuyer(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return resolve(
    messDoorClient.post<
      ImResponse<{
        failNum: number;
        successNum: number;
      }>
    >('/oversea-buyer-offline/v2/importMalBuyerFromExcel', formData),
  );
}

export function getImportMaliciousBuyerHistory(body: BuyerPageParams) {
  return resolve(
    messDoorClient.post<
      ImResponse<{
        records: ReviewAnalysis.ImportMaliciousBuyerHistory[];
        total: number;
      }>
    >('/oversea-buyer-offline/v2/getImportMalBuyerFromExcelHistory', body),
  );
}
